import { cleanObject } from '@shared/utils';
import getPieveCompanyIdForRequest from '@/lib/auth/getPieveCompanyIdForRequest.js';
import { get } from '@/redux/requests/api.js';
import toValidationFailure from '@/redux/requests/lib/toValidationFailure.js';

/**
 * Asks the API for the provider sign-in link of an OAuth app (`GET /v1/oauth/authorize-url`). The
 * link carries a single-use grant and the browser leaves for it at once, so the answer never enters
 * the store: this request has no action on purpose, and resolves the full `{ success, data }`
 * envelope (`data.url`). The loader keeps it out of the GET dedupe cache, which would otherwise hand
 * a second click the same, already spent, link.
 *
 * `returnPath` is the in-app path the OAuth callback sends the person back to (`pieveReturnPath`);
 * `returnOrigin` is this page's origin (`oauthReturnOrigin`). Both ride in the `query` JSON the
 * route expects, and the API keeps them on the grant.
 */
const getOauthAuthorizeUrl = ({ appSlug, credentialId, returnPath, returnOrigin } = {}) => {
  const normalizedAppSlug = String(appSlug || '').trim();
  const normalizedCredentialId = String(credentialId || '').trim();
  const companyId = String(getPieveCompanyIdForRequest() || '').trim();

  if (!normalizedAppSlug) {
    return toValidationFailure('appSlug is required');
  }

  if (!normalizedCredentialId) {
    return toValidationFailure('credentialId is required');
  }

  if (!companyId) {
    return toValidationFailure('Active company is required to connect an app');
  }

  return get('/v1/oauth/authorize-url', {
    loader: 'getOauthAuthorizeUrl',
    query: {
      appSlug: normalizedAppSlug,
      credentialId: normalizedCredentialId,
      companyId,
      query: JSON.stringify(cleanObject({ pieveReturnPath: returnPath, oauthReturnOrigin: returnOrigin }, { strict: true }))
    }
  });
};

export default getOauthAuthorizeUrl;
